import cn from "classnames";
import { getCookie, deleteCookie } from "cookies-next";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect } from "react";

import {
  checkIsPurchasFlowPage,
  checkIsPurchasingProcess,
  checkIsProfilePage,
} from "@config/config";
import { useIsMounted } from "@hooks/useIsMounted";
import { useStore } from "@hooks/useZustandProvider";
import { LOGIN_MODAL, FORGOT_PASSWORD_MODAL } from "@modals/modalNames";
import Divider from "@ui-kit/Divider/Divider";
import { checkIsTicketsFlowPage } from "@utils/utils";

import s from "./MainLayout.module.scss";

const Header = dynamic(() => import("@components/HeaderNew/Header"), {
  ssr: false,
});

const Footer = dynamic(() => import("@components/Footer/Footer"), {
  ssr: false,
});

const Analytics = dynamic(() => import("@components/Analytics/Analytics"), {
  ssr: false,
});

export const MainLayout = ({ children }) => {
  const isMounted = useIsMounted();
  const { route, query, pathname, asPath, ...router } = useRouter();
  const isTicketsFlow = checkIsTicketsFlowPage(route);

  const isPurchasFlow = checkIsPurchasFlowPage(route);
  const isPurchasFlowProcess = checkIsPurchasingProcess(route);
  const isProfilePage = checkIsProfilePage(route);

  const token = getCookie("token");
  const corporateToken = getCookie("corporate-token");

  const {
    resetModals,
    updateApp,
    resetProfile,
    resetTickets,
    resetChangeTickets,
    user,
    cms,
    resetPayment,
  } = useStore();

  const { isEditMode } = cms.uiOptions;
  const { isWidgetEditing } = cms.widget;

  // useEffect(() => {
  //   if (extraLogout) {
  //     resetModals({
  //       isOpenLoginModal: true,
  //     });

  //     deleteCookie("token");

  //     deleteCookie("extraLogout");

  //     // if (!isTicketsFlow) router.push("/");
  //   }
  // }, [extraLogout]);

  useEffect(() => {
    if (!token && !corporateToken) {
      resetProfile(); // do logout action user / admin with reset cms options
    }
  }, [token, corporateToken]);

  // useEffect(() => {
  //   const timerId = setInterval(() => {
  //     const freshExtraLogout = getCookie("extraLogout");

  //     if (freshExtraLogout !== extraLogout) {
  //       setExtraLogout(freshExtraLogout);
  //     }
  //   }, 30);

  //   return () => clearInterval(timerId);
  // }, []);

  // useEffect(() => {
  //   const timerId = setInterval(() => {
  //     const extraLogout = getCookie("extraLogout");
  //     // const token = getCookie("token");
  //     // if (initToken !== token) {

  //     if (extraLogout) {
  //       resetProfile();

  //       resetModals({
  //         isOpenLoginModal: true,
  //       });

  //       deleteCookie("token");

  //       deleteCookie("extraLogout");

  //       // if (!isTicketsFlow) router.push("/");
  //     }

  //     if (user.email && !getCookie("token")) {
  //       deleteCookie("token");
  //       resetProfile();
  //     }
  //   }, 250);

  //   return () => clearInterval(timerId);
  // }, []);

  // useEffect(() => {
  //   const timerId = setInterval(() => {
  //     const extraLogout = getCookie("extraLogout");
  //     // const token = getCookie("token");
  //     // if (initToken !== token) {
  useEffect(() => {
    const timerId = setInterval(() => {
      const extraLogout = getCookie("extraLogout");
      const hasToken = getCookie(
        user.isCorporate ? "corporate-token" : "token",
      );
      const onProfilePage = /\/profile(\/|$)/.test(window.location.pathname);

      if (extraLogout) {
        resetProfile();
        resetModals({
          isOpenLoginModal: true,
        });
        deleteCookie("token");
        deleteCookie("corporate-token");
        deleteCookie("isCorporate");
        deleteCookie("extraLogout");

        if (onProfilePage) {
          router.push("/");
        }
        return;
      }

      if ((user.email || user.isCorporate) && !hasToken) {
        resetProfile();
      }
    }, 250);

    return () => clearInterval(timerId);
  }, [user.email, user.isCorporate, resetProfile, resetModals, router.push]);

  useEffect(() => {
    resetModals();
  }, []);

  useEffect(() => {
    const { modal, ...otherQueries } = query;

    if (modal) {
      if (LOGIN_MODAL.toLowerCase() === modal.toLowerCase()) {
        resetModals({
          isOpenLoginModal: true,
        });
      }

      if (FORGOT_PASSWORD_MODAL.toLowerCase() === modal.toLowerCase()) {
        resetModals({
          isOpenForgotPasswordModal: true,
        });
      }

      router.replace({
        pathname,
        query: otherQueries,
      });
    }
  }, [query]);

  useEffect(() => {
    // Function to notify other tabs about flow change
    const notifyFlowChange = (isEntering) => {
      const message = {
        type: isEntering ? "ENTERING_PURCHASE_FLOW" : "LEAVING_PURCHASE_FLOW",
        timestamp: Date.now(),
      };

      // Try BroadcastChannel first
      if (typeof BroadcastChannel !== "undefined") {
        const channel = new BroadcastChannel("luxexpress-flow-channel");
        channel.postMessage(message);
        channel.close();
      } else {
        // Fallback to localStorage
        localStorage.setItem("luxexpress-flow-change", JSON.stringify(message));
        setTimeout(() => {
          localStorage.removeItem("luxexpress-flow-change");
        }, 1000);
      }
    };

    if (!isPurchasFlow) {
      // Notify other tabs that this tab is leaving purchase flow (starting fresh)
      notifyFlowChange(false);

      resetTickets();
      resetChangeTickets();
      deleteCookie("shopping-basket-id");
      deleteCookie("msi");
      resetPayment();

      document.body.classList.remove("tickets-flow");
    } else {
      // Notify other tabs that this tab is entering purchase flow
      notifyFlowChange(true);

      document.body.classList.add("tickets-flow");
    }

    updateApp({
      isPurchasFlow,
    });
  }, [isPurchasFlow]);

  const isManageBookingPage = route.startsWith("/manage-booking/");

  const isGrayBackground =
    isPurchasFlow || isProfilePage || isManageBookingPage;

  return (
    <>
      <Head>
        <title>Luxexpress</title>
        <meta
          name="viewport"
          content="initial-scale=1.0, width=device-width, maximum-scale=1, viewport-fit=cover"
        />
      </Head>
      <div
        className={cn(
          s.wrapper,
          isGrayBackground && s.ticketsFlow,
          isMounted && isEditMode && s.editMode,
          isMounted && isWidgetEditing && s.isWidgetEditing,
        )}
      >
        <main className={s.main}>
          <Header
            isPurchasFlow={isPurchasFlow}
            isPurchasFlowProcess={isPurchasFlowProcess}
          />
          <div className={cn(s.page, isPurchasFlow && s.ticketsFlow)}>
            {children}
          </div>
          {!isWidgetEditing && <Footer />}
          <Divider />
        </main>

        <div id="modal-root" />
        <Analytics />
      </div>
    </>
  );
};
